/**
 * Single source of truth for the languages ALBAVOX supports across mobile
 * and web. Codes match `src/constants/languages.ts` in the mobile app exactly
 * — including the non-standard `al` (Albanian) and `dk` (Danish), which are
 * intentionally kept as-is for cross-platform consistency.
 */

export type LanguageCode =
  | "en"
  | "de"
  | "al"
  | "dk"
  | "it"
  | "fr"
  | "pl"
  | "el"
  | "cs"
  | "es"
  | "nl"
  | "sv"
  | "no"
  | "mk"
  | "me"
  | "ar"
  | "tr"
  | "he"
  | "ru"
  | "hu";

export type Language = {
  /** Internal code used everywhere (URL prefix, localStorage, i18n). */
  code: LanguageCode;
  /** English name. */
  name: string;
  /** Native name shown in the language picker. */
  nativeName: string;
  /** Flag emoji shown next to the name. */
  flag: string;
  /** BCP-47 code used in `<html lang>` and Accept-Language requests. */
  acceptLanguageCode: string;
  /** Right-to-left scripts (Arabic, Hebrew). */
  rtl: boolean;
};

export const SUPPORTED_LANGUAGES: ReadonlyArray<Language> = [
  { code: "en", name: "English",     nativeName: "English",     flag: "🇬🇧", acceptLanguageCode: "en-GB", rtl: false },
  { code: "de", name: "German",      nativeName: "Deutsch",     flag: "🇩🇪", acceptLanguageCode: "de-DE", rtl: false },
  { code: "al", name: "Albanian",    nativeName: "Shqip",       flag: "🇦🇱", acceptLanguageCode: "sq-AL", rtl: false },
  { code: "dk", name: "Danish",      nativeName: "Dansk",       flag: "🇩🇰", acceptLanguageCode: "da-DK", rtl: false },
  { code: "it", name: "Italian",     nativeName: "Italiano",    flag: "🇮🇹", acceptLanguageCode: "it-IT", rtl: false },
  { code: "fr", name: "French",      nativeName: "Français",    flag: "🇫🇷", acceptLanguageCode: "fr-FR", rtl: false },
  { code: "pl", name: "Polish",      nativeName: "Polski",      flag: "🇵🇱", acceptLanguageCode: "pl-PL", rtl: false },
  { code: "el", name: "Greek",       nativeName: "Ελληνικά",    flag: "🇬🇷", acceptLanguageCode: "el-GR", rtl: false },
  { code: "cs", name: "Czech",       nativeName: "Čeština",     flag: "🇨🇿", acceptLanguageCode: "cs-CZ", rtl: false },
  { code: "es", name: "Spanish",     nativeName: "Español",     flag: "🇪🇸", acceptLanguageCode: "es-ES", rtl: false },
  { code: "nl", name: "Dutch",       nativeName: "Nederlands",  flag: "🇳🇱", acceptLanguageCode: "nl-NL", rtl: false },
  { code: "sv", name: "Swedish",     nativeName: "Svenska",     flag: "🇸🇪", acceptLanguageCode: "sv-SE", rtl: false },
  { code: "no", name: "Norwegian",   nativeName: "Norsk",       flag: "🇳🇴", acceptLanguageCode: "no-NO", rtl: false },
  { code: "mk", name: "Macedonian",  nativeName: "Македонски",  flag: "🇲🇰", acceptLanguageCode: "mk-MK", rtl: false },
  { code: "me", name: "Montenegrin", nativeName: "Crnogorski",  flag: "🇲🇪", acceptLanguageCode: "sr-ME", rtl: false },
  { code: "ar", name: "Arabic",      nativeName: "العربية",     flag: "🇸🇦", acceptLanguageCode: "ar-SA", rtl: true },
  { code: "tr", name: "Turkish",     nativeName: "Türkçe",      flag: "🇹🇷", acceptLanguageCode: "tr-TR", rtl: false },
  { code: "he", name: "Hebrew",      nativeName: "עברית",       flag: "🇮🇱", acceptLanguageCode: "he-IL", rtl: true },
  { code: "ru", name: "Russian",     nativeName: "Русский",     flag: "🇷🇺", acceptLanguageCode: "ru-RU", rtl: false },
  { code: "hu", name: "Hungarian",   nativeName: "Magyar",      flag: "🇭🇺", acceptLanguageCode: "hu-HU", rtl: false },
] as const;

export const DEFAULT_LANGUAGE: LanguageCode = "en";
export const FALLBACK_LANGUAGE: LanguageCode = "en";
export const STORAGE_KEY = "albavox-lang";

const CODE_LOOKUP = new Map<string, Language>(
  SUPPORTED_LANGUAGES.map((l) => [l.code, l]),
);

export function getLanguageByCode(code: string | undefined | null): Language {
  if (!code) return CODE_LOOKUP.get(DEFAULT_LANGUAGE)!;
  return CODE_LOOKUP.get(code) ?? CODE_LOOKUP.get(DEFAULT_LANGUAGE)!;
}

export function isSupportedLanguage(code: string): code is LanguageCode {
  return CODE_LOOKUP.has(code);
}

export function isRTLLanguage(code: string): boolean {
  const lang = CODE_LOOKUP.get(code);
  return lang?.rtl ?? false;
}

/**
 * Map a `navigator.language` value (e.g. "sq-AL", "da", "ar-EG") onto one of
 * our supported codes. Handles the non-standard `al`/`dk` aliases.
 */
export function mapNavigatorLanguage(input: string | undefined): LanguageCode {
  if (!input) return DEFAULT_LANGUAGE;
  const lower = input.toLowerCase();
  const primary = lower.split(/[-_]/)[0] ?? lower;

  // Aliases for codes the mobile app stores differently from BCP-47.
  const aliases: Record<string, LanguageCode> = {
    sq: "al",
    da: "dk",
    nb: "no",
    nn: "no",
    sr: "me",
    cnr: "me",
    iw: "he", // legacy Hebrew code
  };
  if (primary in aliases) return aliases[primary]!;

  if (isSupportedLanguage(primary)) return primary;
  return DEFAULT_LANGUAGE;
}
